import RichTextEditorActiveStylesContextProvider from "@src/features/libraryItem/course/contexts/RichTextEditorActiveStylesContext";
import EditorActionsOld from "@src/features/libraryItem/course/EditorActionsOld";
import useClickOutside from "@src/lib/hooks/useClickOutside";
import { FC, useRef } from "react";
import RichTextEditor from "../libraryItem/course/RichTextEditor";
import { TrainingResourceImageLinkBubbleMenu } from "../libraryItem/course/TrainingResourceImageLinkBubbleMenu";
import TiptapEditorContextProvider from "../libraryItem/course/contexts/TiptapEditorContext";
import {
  MediaFragment,
  TranslationSetFragment,
} from "@src/fragments.generated";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import Text from "@src/lib/ui/Text";
import LanguageSelect from "@src/lib/ui/LanguageSelect";
import { SentMessage_MessageBlastMediaFragment } from "./SentMessage.generated";
import MessageBlastMediaPreview from "./MessageBlastMediaPreview";
import XIcon from "@src/lib/icons/18px/x";
import usePostMentionItems from "./posts/usePostMentionItems";

type Props = {
  value: string;
  placeholder: string;
  isEditorActive: boolean;
  onMediaChange?: (media?: MediaFragment) => void;
  setIsEditorActive: (isEditorActive: boolean) => void;
  onChange: (val: string) => void;
  disabled?: boolean;
  messageText?: TranslationSetFragment;
  setMessageText?: (text: TranslationSetFragment) => void;
  pinnedMedia?: SentMessage_MessageBlastMediaFragment | null;
  messageBlastMedias?: Set<SentMessage_MessageBlastMediaFragment>;
  setMessageBlastMedias?: (
    newSet: Set<SentMessage_MessageBlastMediaFragment>,
  ) => void;
  translationModeEnabled?: boolean;
  setTranslationModeEnabled?: (v: boolean) => void;
  nonEnglishLanguage?: Language;
  setNonEnglishLanguage?: (x: Language) => void;
};

const MessageBlastRichTextEditorWithEditorActions: FC<Props> = ({
  value,
  pinnedMedia,
  placeholder,
  isEditorActive,
  setIsEditorActive,
  onMediaChange,
  onChange,
  disabled = false,
  messageText,
  setMessageText,
  messageBlastMedias,
  setMessageBlastMedias,
  translationModeEnabled,
  setTranslationModeEnabled,
  nonEnglishLanguage,
  setNonEnglishLanguage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => {
    if (isEditorActive) {
      setIsEditorActive(false);
    }
  });
  const mentionItems = usePostMentionItems();
  return (
    <TooltipProvider>
      <TiptapEditorContextProvider>
        <RichTextEditorActiveStylesContextProvider>
          <div
            ref={containerRef}
            className="relative mb-1 w-full rounded-lg border border-input bg-background shadow-sm"
          >
            <div className="flex flex-col gap-2 p-3">
              {pinnedMedia && (
                <div className="group relative mb-2 w-[240px]">
                  <MessageBlastMediaPreview
                    showRichTextDocPreview
                    messageBlast={pinnedMedia}
                    id={pinnedMedia.id.toString()}
                    className="rounded-lg"
                  />
                  <div className="absolute inset-0 flex items-start justify-end p-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="outline"
                          className="hidden group-hover:flex"
                          onClick={() => {
                            const mediaArray = messageBlastMedias
                              ? Array.from(messageBlastMedias)
                              : [];
                            const updatedArray = mediaArray.map((x) =>
                              x.id.toString() === pinnedMedia.id.toString()
                                ? { ...x, pinned: false }
                                : x,
                            );
                            setMessageBlastMedias?.(new Set(updatedArray));
                          }}
                        >
                          <XIcon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Unpin</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
              <RichTextEditor
                text={value}
                placeholder={placeholder}
                hasEditContentPermission={true}
                className="min-h-[100px] text-sm"
                enableMentions
                mentionItems={mentionItems}
                onFocus={() => setIsEditorActive(true)}
                onChange={onChange}
                disabled={disabled}
                customLinkEditor={TrainingResourceImageLinkBubbleMenu}
              />
            </div>
            {translationModeEnabled &&
              nonEnglishLanguage &&
              setNonEnglishLanguage && (
                <div className="flex items-center justify-end bg-muted px-3 py-2">
                  <Text
                    variant="compact-title"
                    className="w-full text-muted-foreground"
                  >
                    Select a language
                  </Text>
                  <LanguageSelect
                    selectedLanguage={nonEnglishLanguage}
                    onLanguageChange={setNonEnglishLanguage}
                  />
                </div>
              )}
            <div className="border-t border-input py-1">
              <EditorActionsOld
                onMediaChange={onMediaChange}
                messageText={messageText}
                setMessageText={setMessageText}
                translationModeEnabled={translationModeEnabled}
                setTranslationModeEnabled={setTranslationModeEnabled}
              />
            </div>
          </div>
        </RichTextEditorActiveStylesContextProvider>
      </TiptapEditorContextProvider>
    </TooltipProvider>
  );
};

export default MessageBlastRichTextEditorWithEditorActions;
